import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import EquipmentPage from './pages/EquipmentPage';
import FacilitiesPage from './pages/FacilitiesPage';
import VendorsPage from './pages/VendorsPage';
import VendorDetailPage from './pages/VendorDetailPage';
import RequestsPage from './pages/RequestsPage';
import ReceivedStudiesPage from './pages/ReceivedStudiesPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ReportsPage from './pages/ReportsPage';
import EquipmentProcedureMappingPage from './pages/EquipmentProcedureMappingPage';
import ProceduresPage from './pages/ProceduresPage';
import FacilityDetailPage from './pages/FacilityDetailPage';
import EquipmentDetailPage from './pages/EquipmentDetailPage';
import SettingsPage from './pages/settings/SettingsPage';
import SettingsModalitiesPage from './pages/settings/SettingsModalitiesPage';
import SettingsRevenueDistributionPage from './pages/settings/SettingsRevenueDistributionPage';
import SettingsUsersPage from './pages/settings/SettingsUsersPage';
import SettingsErrorLogsPage from './pages/settings/SettingsErrorLogsPage';
import SettingsDicomErrorsPage from './pages/settings/SettingsDicomErrorsPage';
import SettingsLoginHistoryPage from './pages/settings/SettingsLoginHistoryPage';
import SettingsShaVerificationLogsPage from './pages/settings/SettingsShaVerificationLogsPage';
import SettingsPublicAccessPage from './pages/settings/SettingsPublicAccessPage';
import PublicFacilityLookupPage from './pages/PublicFacilityLookupPage';
import EquipmentUsagePage from './pages/EquipmentUsagePage';
import PublicEquipmentUsagePage from './pages/PublicEquipmentUsagePage';
import './styles/index.css';

/**
 * App Component
 * Main app with routing and auth provider
 */
function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/facility-lookup" element={<PublicFacilityLookupPage />} />
          <Route path="/equipment-usage/public" element={<PublicEquipmentUsagePage />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <DashboardPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/facilities"
            element={
              <ProtectedRoute resource="facilities">
                <Layout>
                  <FacilitiesPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/facilities/:id"
            element={
              <ProtectedRoute resource="facilities" hideForVendor>
                <Layout>
                  <FacilityDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/equipment"
            element={
              <ProtectedRoute resource="equipment">
                <Layout>
                  <EquipmentPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/equipment/:id"
            element={
              <ProtectedRoute resource="equipment">
                <Layout>
                  <EquipmentDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/vendors"
            element={
              <ProtectedRoute resource="vendors">
                <Layout>
                  <VendorsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/vendors/:id"
            element={
              <ProtectedRoute resource="vendors">
                <Layout>
                  <VendorDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/requests"
            element={
              <ProtectedRoute resource="requests" hideForVendor>
                <Layout>
                  <RequestsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/received-studies"
            element={
              <ProtectedRoute resource="requests">
                <Layout>
                  <ReceivedStudiesPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/equipment-usage"
            element={
              <ProtectedRoute anyPermissions={[{ resource: 'requests' }, { resource: 'equipment' }]}>
                <Layout>
                  <EquipmentUsagePage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/analytics"
            element={
              <ProtectedRoute
                anyPermissions={[
                  { resource: 'requests' },
                  { resource: 'equipment' },
                  { resource: 'facilities' },
                  { resource: 'vendors' },
                ]}
              >
                <Layout>
                  <AnalyticsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports"
            element={
              <ProtectedRoute
                anyPermissions={[
                  { resource: 'requests' },
                  { resource: 'equipment' },
                  { resource: 'facilities' },
                  { resource: 'vendors' },
                ]}
              >
                <Layout>
                  <ReportsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/procedure-mapping"
            element={
              <ProtectedRoute resource="equipment_mapping">
                <Layout>
                  <EquipmentProcedureMappingPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/procedures"
            element={
              <ProtectedRoute resource="procedures">
                <Layout>
                  <ProceduresPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute
                anyPermissions={[
                  { resource: 'procedures' },
                  { resource: 'equipment_mapping' },
                  { resource: 'revenue_distribution' },
                  { resource: 'users' },
                  { resource: 'permissions' },
                  { resource: 'error_logs' },
                  { resource: 'login_history' },
                ]}
              >
                <Layout>
                  <SettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/error-logs"
            element={
              <ProtectedRoute resource="error_logs">
                <Layout>
                  <SettingsErrorLogsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/dicom-errors"
            element={
              <ProtectedRoute resource="error_logs">
                <Layout>
                  <SettingsDicomErrorsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/sha-verification-logs"
            element={
              <ProtectedRoute resource="error_logs">
                <Layout>
                  <SettingsShaVerificationLogsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/public-usage-access"
            element={
              <ProtectedRoute resource="public_access">
                <Layout>
                  <SettingsPublicAccessPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/login-history"
            element={
              <ProtectedRoute resource="login_history">
                <Layout>
                  <SettingsLoginHistoryPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/modalities"
            element={
              <ProtectedRoute resource="equipment_mapping">
                <Layout>
                  <SettingsModalitiesPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/revenue-distribution"
            element={
              <ProtectedRoute resource="revenue_distribution">
                <Layout>
                  <SettingsRevenueDistributionPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/users"
            element={
              <ProtectedRoute
                anyPermissions={[
                  { resource: 'users' },
                  { resource: 'permissions' },
                ]}
              >
                <Layout>
                  <SettingsUsersPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
